
import { Suspense, lazy, useCallback, useEffect, useState } from "react";
import Login from "./pages/Login"; // match actual filename case for Linux builds
import type { AuthUser } from "./types/auth";
import { useAuth } from "./features/auth/AuthProvider";
import ChatApp from "./routes/chat/ChatRoute";
import { api } from "./lib/api";
import { AppShellSkeleton } from "./components/ui/skeletons";

// Opened on demand, so keep them out of the first load.
const SettingsRoute = lazy(() => import("./routes/settings"));
const ProfileRoute = lazy(() => import("./routes/profile"));
const WelcomeFlow = lazy(() => import("./components/onboarding/WelcomeFlow"));

/* --- Gate component: auth + route shells (chat/settings/profile) --- */
export default function App() {
  const { token, user, status, setAuth } = useAuth();
  const [inviteBusy, setInviteBusy] = useState(false);
  const [showWelcome, setShowWelcome] = useState(false);

  const handleAuth = (t: string, u: AuthUser, _isFirstLogin?: boolean) => {
    setAuth(t, u);
    try {
      localStorage.setItem("user", JSON.stringify(u));
      const rawActivity = (u as { activity?: unknown }).activity;
      const activity = typeof rawActivity === 'string' ? rawActivity : '';
      localStorage.setItem('profile.activity', activity);
      try {
        window.dispatchEvent(new CustomEvent('echo:activity-updated', { detail: activity }));
      } catch { /* noop */ }
    } catch { /* noop */ }
    // Show welcome flow only if not already seen — localStorage is the source of truth
    const welcomed = (() => { try { return !!localStorage.getItem('echo:welcomed'); } catch { return false; } })();
    if (!welcomed) {
      try { localStorage.setItem('echo:welcomed', '1'); } catch { /* noop */ }
      setShowWelcome(true);
    }
  };

  const handleTokenChange = useCallback((nextToken: string | null) => {
    setAuth(nextToken, user);
  }, [setAuth, user]);

  const [routePath, setRoutePath] = useState(() => {
    try {
      return window.location.pathname + window.location.search;
    } catch {
      return "/";
    }
  });

  const parseInviteCode = useCallback((pathLike: string) => {
    const raw = String(pathLike || '');
    const pathname = raw.split('?')[0] || '';
    const m = pathname.match(/^\/invite\/([^/?#]+)/i);
    if (!m || !m[1]) return '';
    try { return decodeURIComponent(m[1]).trim(); } catch { return String(m[1] || '').trim(); }
  }, []);

  useEffect(() => {
    const handler = () => {
      try {
        setRoutePath(window.location.pathname + window.location.search);
      } catch { /* noop */ }
    };
    window.addEventListener('popstate', handler);
    return () => window.removeEventListener('popstate', handler);
  }, []);

  useEffect(() => {
    const code = parseInviteCode(routePath);
    if (!code || token) return;
    try { localStorage.setItem('__pending_invite_code', code); } catch { /* noop */ }
  }, [routePath, token, parseInviteCode]);

  useEffect(() => {
    const pathCode = parseInviteCode(routePath);
    let storedCode = '';
    try { storedCode = String(localStorage.getItem('__pending_invite_code') || '').trim(); } catch { /* noop */ }
    const code = pathCode || storedCode;
    if (!token || !code) return;
    let cancelled = false;
    (async () => {
      setInviteBusy(true);
      try {
        const res = await api.postAuth('/invites/accept', { code }, token);
        const sid = String(res?.spaceId || '').trim();
        if (sid) {
          try {
            localStorage.setItem('__pending_invite_target', JSON.stringify({ spaceId: sid }));
            window.dispatchEvent(new CustomEvent('echo:invite-target'));
          } catch { /* noop */ }
        }
        try { localStorage.removeItem('__pending_invite_code'); } catch { /* noop */ }
      } catch {
        try { localStorage.removeItem('__pending_invite_code'); } catch { /* noop */ }
      } finally {
        if (!cancelled) {
          try {
            window.history.replaceState({}, '', '/');
            setRoutePath('/');
          } catch {
            window.location.href = '/';
          }
          setInviteBusy(false);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [token, routePath, parseInviteCode]);

  const openProfile = useCallback((targetId: string) => {
    if (!targetId) return;
    const url = `/profile/${encodeURIComponent(targetId)}`;
    try {
      window.history.pushState({}, '', url);
      setRoutePath(url);
    } catch {
      window.location.href = url;
    }
  }, []);

  const openSettings = useCallback((section?: string) => {
    const url = section ? `/settings/${encodeURIComponent(section)}` : '/settings';
    try {
      window.history.pushState({}, '', url);
      setRoutePath(url);
    } catch {
      window.location.href = url;
    }
  }, []);

  const profileRoute = routePath.startsWith("/profile") ? routePath : null;
  const exitProfileRoute = useCallback(() => {
    if (!profileRoute) return;
    try {
      window.history.pushState({}, '', '/');
      setRoutePath('/');
    } catch {
      window.location.href = '/';
    }
  }, [profileRoute]);

  const settingsRoute = routePath.startsWith("/settings") ? routePath : null;
  const exitSettingsRoute = useCallback(() => {
    if (!settingsRoute) return;
    try {
      window.history.pushState({}, '', '/');
      setRoutePath('/');
    } catch {
      window.location.href = '/';
    }
  }, [settingsRoute]);

  if (status === "checking") return <AppShellSkeleton />;
  if (!token) {
    const searchParams = (() => { try { return new URL(window.location.href).searchParams; } catch { return new URLSearchParams(); } })();
    // Handle email verification redirects: ?verify=success|expired|invalid
    const verifyParam = searchParams.get('verify') || '';
    if (verifyParam) {
      try { window.history.replaceState({}, '', '/'); } catch {}
      return <VerifyFeedback status={verifyParam} />;
    }
    // Handle password reset links: ?reset=<token>
    const resetToken = searchParams.get('reset') || '';
    if (resetToken) {
      try { window.history.replaceState({}, '', '/'); } catch {}
      return <ResetPassword token={resetToken} onDone={() => window.location.reload()} />;
    }
    return <Login onAuth={handleAuth} />;
  }
  if (inviteBusy) {
    return <AppShellSkeleton />;
  }

  return (
    <>
      <ChatApp
        token={token}
        user={user}
        onTokenChange={handleTokenChange}
        onOpenProfile={openProfile}
        onDismissProfileRoute={exitProfileRoute}
        onOpenSettings={openSettings}
        onDismissSettingsRoute={exitSettingsRoute}
      />
      {showWelcome && (
        <Suspense fallback={null}>
        <WelcomeFlow
          userName={user?.name || user?.username || ''}
          onDone={(action) => {
            setShowWelcome(false);
            if (action === 'create') {
              // Small delay to let the overlay close before dispatching
              setTimeout(() => {
                try { window.dispatchEvent(new CustomEvent('echo:open-create-space')); } catch {}
              }, 150);
            } else if (action === 'find') {
              try { window.dispatchEvent(new CustomEvent('echo:open-join-space')); } catch {}
            }
          }}
        />
        </Suspense>
      )}
      {settingsRoute && (
        <div className="fixed inset-y-0 left-0 right-0 z-[5500] md:left-16" style={{ top: (window as any).echoDesktop ? 36 : 0 }}>
          <div className="overlay-fade absolute inset-0 bg-black/70 md:bg-black/60 md:backdrop-blur-md" onClick={exitSettingsRoute} />
          <div className="overlay-slide-in relative h-full overflow-y-auto overflow-x-hidden bg-neutral-950 border-l border-neutral-800 md:rounded-l-3xl">
            <button
              className="absolute top-4 right-4 z-10 rounded-full border border-white/10 bg-black/60 p-2 text-fg-2 hover:text-white"
              onClick={exitSettingsRoute}
              aria-label="Close settings"
              title="Close settings"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-5 w-5">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
            <Suspense fallback={<div className="p-6 text-sm text-fg-3">Loading…</div>}>
              <SettingsRoute />
            </Suspense>
          </div>
        </div>
      )}
      {profileRoute && (
        <div className="fixed inset-0 z-[6000] flex items-center justify-center px-2 sm:px-4 overflow-hidden">
          <div className="overlay-fade absolute inset-0 bg-black/75 md:bg-[#020608]/65 md:backdrop-blur-lg" onClick={exitProfileRoute} />
          <div className="overlay-pop relative flex h-full w-full max-w-6xl flex-col overflow-hidden rounded-[2rem] border border-white/[0.07] bg-[#080d10]/95 shadow-[0_30px_90px_rgba(0,0,0,0.72),0_0_70px_rgba(20,184,166,0.08)] md:h-[92vh]">
            <button
              className="absolute right-4 top-4 z-10 rounded-full border border-white/[0.08] bg-black/35 p-2.5 text-fg-2 shadow-lg backdrop-blur-md transition hover:border-emerald-300/20 hover:bg-emerald-300/10 hover:text-white"
              onClick={exitProfileRoute}
              aria-label="Close profile"
              title="Close profile"
              type="button"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-4 w-4">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
            <div className="flex-1 overflow-y-auto">
              <Suspense fallback={<div className="p-6 text-sm text-fg-3">Loading…</div>}>
                <ProfileRoute token={token} onClose={exitProfileRoute} />
              </Suspense>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function ResetPassword({ token, onDone }: { token: string; onDone: () => void }) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [done, setDone] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (password !== confirm) { setErr('Passwords do not match.'); return; }
    setBusy(true); setErr('');
    try {
      await (await import('./lib/api')).api.post('/auth/reset-password', { token, password });
      setDone(true);
    } catch (e: any) {
      setErr(e?.message || 'Failed to reset password. The link may have expired.');
    } finally { setBusy(false); }
  }

  const rules = [
    { label: 'At least 8 characters', met: password.length >= 8 },
    { label: 'One letter',  met: /[A-Z]/.test(password) },
    { label: 'One lowercase letter',  met: /[a-z]/.test(password) },
    { label: 'One number',            met: /[0-9]/.test(password) },
  ];

  return (
    <div className="flex flex-col items-center justify-center min-h-screen text-fg brand-login-bg p-6">
      <div className="mb-4 flex flex-col items-center gap-2 select-none">
        <img src="/brand/Echo_logo_plant_sm.png" alt="Echo" width="104" height="168" className="h-14 w-auto object-contain" loading="lazy" />
        <div className="text-lg tracking-wide font-medium text-emerald-300">Echo</div>
      </div>
      <div className="bg-white/[0.04] border border-white/10 p-6 rounded-2xl w-full max-w-xs space-y-4">
        {done ? (
          <div className="text-center space-y-3">
            <div className="flex justify-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500/15 border border-emerald-500/30">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-7 w-7 text-emerald-400"><path d="M20 6 9 17l-5-5"/></svg>
              </span>
            </div>
            <h2 className="text-lg font-medium text-white">Password updated</h2>
            <p className="text-sm text-fg-2">Your password has been changed. You can now log in with your new password.</p>
            <button onClick={onDone} className="w-full py-2.5 rounded-md text-white bg-brand hover:brightness-110 transition-colors text-sm font-medium">
              Go to login
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div className="text-center">
              <h2 className="text-xl font-medium text-fg">Set new password</h2>
            </div>
            {err && <div className="text-red-400 text-sm">{err}</div>}
            <div>
              <input
                type="password"
                placeholder="New password"
                aria-label="New password"
                autoComplete="new-password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="w-full p-2.5 rounded-md bg-neutral-900 text-fg placeholder-neutral-500 border border-neutral-800 focus:outline-none focus:ring-2 focus:ring-emerald-600/60"
              />
              {password && (
                <ul className="mt-1.5 space-y-0.5 text-meta">
                  {rules.map(r => (
                    <li key={r.label} className={r.met ? 'text-emerald-400' : 'text-fg-3'}>
                      {r.met ? '✓' : '·'} {r.label}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <input
              type="password"
              placeholder="Confirm new password"
              aria-label="Confirm new password"
              autoComplete="new-password"
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              className="w-full p-2.5 rounded-md bg-neutral-900 text-fg placeholder-neutral-500 border border-neutral-800 focus:outline-none focus:ring-2 focus:ring-emerald-600/60"
            />
            <button
              type="submit"
              disabled={busy || !password || !confirm}
              className="w-full py-2.5 rounded-md text-white bg-brand hover:brightness-110 transition-colors disabled:opacity-60"
            >
              {busy ? 'Updating…' : 'Set new password'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

function VerifyFeedback({ status }: { status: string }) {
  const success = status === 'success';
  const expired = status === 'expired';
  return (
    <div className="flex flex-col items-center justify-center min-h-screen text-fg brand-login-bg p-6">
      <div className="mb-4 flex flex-col items-center gap-2 select-none">
        <img src="/brand/Echo_logo_plant_sm.png" alt="Echo" width="104" height="168" className="h-14 w-auto object-contain" loading="lazy" />
        <div className="text-lg tracking-wide font-medium text-emerald-300">Echo</div>
      </div>
      <div className="bg-white/[0.04] border border-white/10 p-6 rounded-2xl w-full max-w-xs space-y-4 text-center">
        <div className="flex justify-center">
          <span className={`flex h-14 w-14 items-center justify-center rounded-full border ${success ? 'bg-emerald-500/15 border-emerald-500/30' : 'bg-red-500/15 border-red-500/30'}`}>
            {success ? (
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-7 w-7 text-emerald-400"><path d="M20 6 9 17l-5-5"/></svg>
            ) : (
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="h-7 w-7 text-red-400"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
            )}
          </span>
        </div>
        <h2 className="text-lg font-medium text-white">
          {success ? 'Email verified!' : expired ? 'Link expired' : 'Invalid link'}
        </h2>
        <p className="text-sm text-fg-2">
          {success
            ? 'Your email address has been verified. You can now log in to Echo.'
            : expired
            ? 'This verification link has expired. Request a new one from the login screen.'
            : 'This verification link is invalid. It may have already been used.'}
        </p>
        <a
          href="/"
          className="inline-block w-full py-2.5 rounded-md text-center text-white bg-brand hover:brightness-110 transition-colors text-sm font-medium"
        >
          {success ? 'Go to login' : 'Back to login'}
        </a>
      </div>
    </div>
  );
}
